import { FC, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import {
  Title,
  Group,
  Divider,
  Checkbox,
  TextInput,
  SimpleGrid,
  createStyles,
} from "@mantine/core";

import { AddressInput } from "@components/AddressInput/AddressInput";
import { EntityTypeInput } from "@components/EntityTypeInput/EntityTypeInput";

import { Draft } from "@utils/Draft.util";
import { InputProps } from "@utils/InputProps.util";
import { IContactAndBilling } from "@models/EventRegistrationForm.model";

const entityTypeNameLabel: { [key: string]: string } = {
  person: "Individual",
  organization: "Organization",
  company: "Company",
  group: "Group",
};

const useStyles = createStyles((t) => ({
  grid: {
    gridTemplateColumns: "repeat(2, 1fr)",
    [`@media (max-width: ${t.breakpoints.xs}px)`]: {
      gridTemplateColumns: "1fr",
    },
  },
}));

export const ContactAndBillingInput: FC<
  InputProps<Draft<IContactAndBilling>>
> = (props) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const isAdditionToInvoiceChecked =
    props.billingAddress?.options?.value?.includes("addition");
  const isContactPersonOnInvoiceChecked =
    props.billingAddress?.options?.value?.includes("contact");

  const $toRefs = { props };
  const toRefs = useRef($toRefs);
  useEffect(() => {
    toRefs.current = $toRefs;
  });

  useEffect(() => {
    if (!isAdditionToInvoiceChecked)
      toRefs.current.props.billingAddress?.additionToInvoice?.onChange("");
  }, [isAdditionToInvoiceChecked]);

  useEffect(() => {
    if (!isContactPersonOnInvoiceChecked)
      toRefs.current.props.billingAddress?.contactPersonOnInvoice?.onChange("");
  }, [isContactPersonOnInvoiceChecked]);

  return (
    <>
      <EntityTypeInput required {...props.address?.type} />
      {props.address?.type?.value && (
        <TextInput
          required
          label={
            <>
              {t(entityTypeNameLabel[props.address.type.value])} {t("Name")}
            </>
          }
          {...props.name}
        />
      )}
      <SimpleGrid className={classes.grid}>
        <TextInput
          required
          label={
            <>
              {t("Contact Person")} {t("First Name")}
            </>
          }
          {...props.firstName}
        />
        <TextInput
          required
          label={
            <>
              {t("Contact Person")} {t("Last Name")}
            </>
          }
          {...props.lastName}
        />
      </SimpleGrid>
      <AddressInput {...props.address} />
      <Divider />
      <Title order={5}>{t("Billing Address")}</Title>
      <Checkbox
        label={t(
          "The billing address is identical with the registered {{selected}} address.",
          {
            selected: props.address?.type?.value
              ? entityTypeNameLabel[props.address.type.value].toLowerCase()
              : "",
          }
        )}
        {...props.isBillingAddressSame}
        checked={props.isBillingAddressSame?.value}
      />
      {!props.isBillingAddressSame?.value && (
        <>
          <EntityTypeInput required {...props.billingAddress?.type} />
          {props.billingAddress?.type?.value && (
            <TextInput
              required
              label={
                <>
                  {t(entityTypeNameLabel[props.billingAddress.type.value])}{" "}
                  {t("Name")}
                </>
              }
              {...props.billingAddress.name}
            />
          )}
          <Checkbox.Group
            orientation="vertical"
            {...props.billingAddress?.options}
          >
            <Group>
              <Checkbox
                value="addition"
                label={t("Possibly addition to the invoice")}
              />
              {props.billingAddress?.options?.value?.includes("addition") && (
                <TextInput
                  size="xs"
                  required
                  {...props.billingAddress.additionToInvoice}
                />
              )}
            </Group>
            <Group>
              <Checkbox
                value="contact"
                label={t("If necessary, contact person on invoice")}
              />
              {props.billingAddress?.options?.value?.includes("contact") && (
                <TextInput
                  size="xs"
                  required
                  {...props.billingAddress.contactPersonOnInvoice}
                />
              )}
            </Group>
          </Checkbox.Group>
          <AddressInput {...props.billingAddress} />
        </>
      )}
    </>
  );
};
